import { type FC, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  Chip,
  DropdownCard,
  DropdownContent,
  type IChipProps,
  type IDropdownItem,
  Scrollbar,
  Typography,
} from 'shared/ui'
import { TextField, makeTextFieldAutoFocus } from 'shared/ui/TextField'

import styles from './styles.module.scss'

export type INewInboxDropdownContentProps = {
  selectedItems?: IDropdownItem[]
  items: IDropdownItem[]
  onDelete?: (item: IDropdownItem) => void
  onSelect?: (items: IDropdownItem) => void
  ChipComponent?: FC<IChipProps>
  notFoundText?: string
  shouldShowNotFoundText?: boolean
  placeholder?: string
  withEmailSearch?: boolean
  scrollBarMaxHeight?: number
}

export const MultiSelectDropdownContent = observer(
  ({
    items,
    selectedItems,
    onDelete,
    onSelect,
    ChipComponent = Chip,
    notFoundText = 'No results found',
    shouldShowNotFoundText,
    placeholder = 'Search',
    withEmailSearch = false,
    scrollBarMaxHeight,
  }: INewInboxDropdownContentProps) => {
    const [search, setSearch] = useState('')

    const selectedIds = useMemo(
      () => new Set(selectedItems?.map((item) => item.id) || []),
      [selectedItems]
    )

    const showItems = useMemo(() => {
      const lowercasedSearch = search.toLowerCase()

      return items
        .filter((item) => {
          if (selectedIds.has(item.id)) return false
          if (!search) return true

          const labelMatch = item.label.toLowerCase().includes(lowercasedSearch)
          const emailMatch = item.data?.email?.toLowerCase().includes(lowercasedSearch) ?? false

          return (withEmailSearch && emailMatch) || labelMatch
        })
        .map((item) => ({ ...item, disabledClosedTooltip: true }))
    }, [items, search, selectedIds])

    return (
      <DropdownContent
        className={classNames(styles.dropdownContentWrap, 'top-dropdown-column-reverse-content')}
      >
        <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={scrollBarMaxHeight ?? undefined}>
          <div className={styles.selectedItemsWrap} aria-label='AddedItemsList'>
            {selectedItems?.map((item) => (
              <ChipComponent
                key={item.id}
                item={item}
                label={item.label}
                iconType='div'
                onDelete={() => onDelete?.(item)}
              />
            ))}
            <div style={{ width: '100%' }}>
              <TextField
                variant='integrated'
                className={styles.search}
                value={search}
                onChange={(key) => setSearch(key)}
                InputProps={{
                  placeholder: placeholder,
                }}
                ariaLabel='ItemsTextFieldSearchInput'
                mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
              />
            </div>
          </div>
        </Scrollbar>
        <div className={styles.divider}></div>
        <div className={styles.searchItemsListWrap} aria-label='ItemsListItems'>
          <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={300}>
            <div className={styles.searchTagsList}>
              {showItems.map((item) => (
                <DropdownCard
                  key={item.id}
                  item={item}
                  onChange={(item) => {
                    onSelect?.(item)
                    setSearch('')
                  }}
                />
              ))}
            </div>
            {!showItems.length && shouldShowNotFoundText && (
              <div className={styles.noResult}>
                <Typography
                  ariaLabel='NoResultsText'
                  variant='body-sm-regular'
                  color='var(--content-primary-tertiary)'
                >
                  {notFoundText}
                </Typography>
              </div>
            )}
          </Scrollbar>
        </div>
      </DropdownContent>
    )
  }
)
